import { captureMessage, ErrorBoundary } from '@sentry/react'
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Actions } from '~/actions'
import Button from '~/components/Button'
import { Caret, Close, CountryIcon } from '~/components/Icons'
import { Portal } from '~/components/utils'
import {
	countryList,
	extractAttributes,
	getMarketSwitchConfig,
	getPrefferedCountryCode,
	getStoreCountry,
	includes,
	isShop,
	MarketSwitchStatus,
	useDispatch,
	useQuerySelector,
	useSelector
} from '~/utils'
import { CountryContext } from './CountryProvider'
import { useTranslations } from './LanguageProvider'

interface Props {
	headerOpen: boolean
}

export const CountrySelector: React.FC<Props> = () => {
	const existingForm = useQuerySelector<HTMLFormElement>('#lform-wrapper > #localization_form', {
		attributeFilter: ['class']
	})
	const [{ attributes, inputs }, setAttributes] = useState({
		attributes: {},
		inputs: [] as Array<Record<string, string>>
	})
	const dispatch = useDispatch()
	const t = useTranslations('react.actions')
	const markets = useSelector(s => s.markets)
	const cartCountry = useSelector(s => s.cart?.buyerIdentity?.countryCode)
	const { country: current, setCountry } = useContext(CountryContext)
	const [open, setOpen] = useState(false)
	const [ready, setReady] = useState(false)
	const formRef = useRef<HTMLFormElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	useEffect(() => {
		// Wait for localization guessing to complete
		const interval = setInterval(() => {
			if (!window.localization.ready) return
			const pref = getPrefferedCountryCode()
			const country = countryList.find(c => c.iso_code === pref)
			if (country && getStoreCountry() === 'EU') setCountry(country)
			setTimeout(() => setReady(true), 200)
			clearInterval(interval)
		}, 50)
		return () => clearInterval(interval)
	}, [setCountry])

	useLayoutEffect(() => {
		if (markets === null) return
		console.debug({
			current: current.iso_code,
			locale: window.localization.country.iso_code,
			cookie: getPrefferedCountryCode(),
			store: getStoreCountry(),
			ready
		})
		const currentCode = current.iso_code
		if (inputRef.current) inputRef.current.value = currentCode
		if (currentCode === undefined) {
			// Do nothing
		} else if (window.Shopify.theme.role !== 'main' || window.Shopify.designMode) {
			// Don't do full market switch in dev modes
			if ('unit_system' in current && formRef.current)
				dispatch(Actions.updateCartCountry(current, formRef.current))
		} else if (currentCode == 'EU') {
			if (getStoreCountry() === 'EU' && ready) setOpen(true)
		} else if (currentCode !== window.localization.country.iso_code) {
			if (!formRef.current) {
				captureMessage('#localization_form missing', 'error')
			} else {
				const cfg = getMarketSwitchConfig(markets, window.localization.country.iso_code, currentCode)
				console.debug(cfg)
				switch (cfg.status) {
					case MarketSwitchStatus.Store:
					case MarketSwitchStatus.Domain:
					case MarketSwitchStatus.Currency: {
						const domain = cfg.nextMarket.webPresences[0]?.domain
						if (domain) {
							const url = new URL(`https://${domain}${location.pathname}`)
							/** @TODO only set country param if navigating to store with multiple countries */
							url.searchParams.set('country', currentCode)
							console.info('Redirecting to', url.toString())
							location.replace(url.toString())
						} else {
							console.error('No domain found for market', cfg.nextMarket)
						}
						break
					}
					case MarketSwitchStatus.None:
						if ('unit_system' in current && formRef.current)
							dispatch(Actions.updateCartCountry(current, formRef.current))
						setOpen(false)
						break
				}
			}
		}
		if (currentCode !== cartCountry && 'unit_system' in current) {
			dispatch(Actions.updateCartBuyerIdentity(current))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Ignore cartCountry to avoid unnecessary triggers
	}, [markets, current, ready, dispatch])

	useEffect(() => {
		if (!existingForm) return
		// Excract values from form generated by Shopify
		setAttributes({
			attributes: extractAttributes(existingForm),
			inputs: Array.from(existingForm.children)
				.filter(el => !includes(el.attributes.getNamedItem('name')?.value, 'return_to', 'country_code'))
				.map(extractAttributes)
		})
		existingForm.parentElement?.remove()
	}, [existingForm])

	if (!attributes || !ready) return null

	return (
		<ErrorBoundary>
			{(current.iso_code !== 'EU' || isShop()) && (
				<button
					className={
						'button flex items-center space-x-2 rounded-full p-2 ' +
						'hocus:shadow hocus:shadow-black/20 transition-shadow'
					}
					onClick={_ => setOpen(true)}
					aria-label={current.name}
				>
					<CountryIcon isoCode={current.iso_code} />
					<Caret height="6px" />
				</button>
			)}
			<Portal
				open={open}
				modal
				onClick={_ => setOpen(false)}
				onClose={() => setOpen(false)}
				className="relative flex flex-col xl:p-12"
			>
				<h2 className="mb-6 text-center text-5xl md:mb-12">{t?.select_country}</h2>
				<ul
					className="list grid grid-cols-2 gap-2 overflow-scroll p-2 sm:grid-cols-3 xl:grid-cols-4"
					role="listbox"
				>
					{countryList.map((country, i) => {
						const { status, nextMarket } = getMarketSwitchConfig(
							markets ?? [],
							current.iso_code,
							country.iso_code
						)
						const domain = nextMarket?.webPresences[0]?.domain
						return (
							<li key={i} role="option" data-country={country.iso_code}>
								<Button
									className={
										'button hocus:bg-white w-full rounded-xl bg-white p-2 text-black hover:drop-shadow ' +
										'flex items-center space-x-4 text-left'
									}
									tabIndex={0}
									href={
										status !== MarketSwitchStatus.None
											? `https://${domain}${location.pathname}?country=${country.iso_code}`
											: undefined
									}
									onClick={() => {
										const nextCountry =
											countryList.find(c => c.iso_code === country.iso_code) ?? country
										// Some stores may not have all countries available, but in that case we will switch stores anyways
										setCountry(nextCountry)
										if (inputRef.current) inputRef.current.value = nextCountry.iso_code
										// Only close popup if country switch would happen instantly
										if (status === MarketSwitchStatus.None) {
											const url = new URL(window.location.href)
											url.searchParams.delete('country')
											window.history.replaceState({}, '', url.toString())
											setOpen(false)
										}
									}}
									autoFocus={i == 0}
								>
									<CountryIcon isoCode={country.iso_code} />
									<span className="text-xl" role="presentation">
										{country.name}
									</span>
								</Button>
							</li>
						)
					})}
				</ul>
				<Button
					className="absolute right-0 top-0 rounded-none rounded-bl-md p-4 text-base leading-none sm:-right-5 sm:-top-5 sm:rounded-full"
					onClick={_ => setOpen(false)}
				>
					<Close className="block" />
				</Button>
			</Portal>
			<form {...attributes} ref={formRef}>
				{inputs.map(attrs => (
					<input key={attrs.name} {...attrs} style={{ display: 'none' }} />
				))}
				<input type="hidden" name="country_code" value={current.iso_code} ref={inputRef} />
				<input type="hidden" name="return_to" value={`${location.pathname}?country=${current.iso_code}`} />
			</form>
		</ErrorBoundary>
	)
}
